import {useQuery} from "@tanstack/react-query"

import api, {queryKey} from "../services"
import {AActivitiesListResult, ACampaignsListData} from "../services/types.generated"

export type TReachRange = {from?: string; to?: string}

type TFlowTypeCounts = {new_biz: number; upsell: number}

export type TReachMeta = {
  lead_pool: {total: number; dormant: number}
  sales_flows: {total: number; active: number; finished: number; planned: number}
  flow_types: {flows: TFlowTypeCounts; leads: TFlowTypeCounts}
  results: {mql: number; sql: number; won: number; sales_team_conversion: number}
  funnel: {leads_in_flows: number; responses: number; mql: number; sql: number; back_in_pool: number}
}

type TApiReachMeta = NonNullable<ACampaignsListData["meta"]>

const normalizeFlowTypeCounts = (counts: {new_biz?: number; upsell?: number} | undefined): TFlowTypeCounts => ({
  new_biz: counts?.new_biz ?? 0,
  upsell: counts?.upsell ?? 0,
})

export function normalizeReachMeta(meta: TApiReachMeta | undefined): TReachMeta {
  return {
    lead_pool: {
      total: meta?.lead_pool?.total ?? 0,
      dormant: meta?.lead_pool?.dormant ?? 0,
    },
    sales_flows: {
      total: meta?.sales_flows?.total ?? 0,
      active: meta?.sales_flows?.active ?? 0,
      finished: meta?.sales_flows?.finished ?? 0,
      planned: meta?.sales_flows?.planned ?? 0,
    },
    flow_types: {
      flows: normalizeFlowTypeCounts(meta?.flow_types?.flows),
      leads: normalizeFlowTypeCounts(meta?.flow_types?.leads),
    },
    results: {
      mql: meta?.results?.mql ?? 0,
      sql: meta?.results?.sql ?? 0,
      won: meta?.results?.won ?? 0,
      sales_team_conversion: meta?.results?.sales_team_conversion ?? 0,
    },
    funnel: {
      leads_in_flows: meta?.funnel?.leads_in_flows ?? 0,
      responses: meta?.funnel?.responses ?? 0,
      mql: meta?.funnel?.mql ?? 0,
      sql: meta?.funnel?.sql ?? 0,
      back_in_pool: meta?.funnel?.back_in_pool ?? 0,
    },
  }
}

export function useReachCampaignsQuery({from, to}: TReachRange) {
  return useQuery({
    queryKey: queryKey.reachCampaigns(from, to),
    queryFn: async () => normalizeReachMeta((await api.reachEngine.campaignsList({from, to})).data.meta),
    staleTime: 5 * 60 * 1000,
  })
}

export type TReachActivityChannel = AActivitiesListResult["channels"][number]

export function useReachActivitiesQuery({from, to}: TReachRange) {
  return useQuery({
    queryKey: queryKey.reachActivities(from, to),
    queryFn: async () => (await api.reachEngine.activitiesList({from, to})).data.channels,
    staleTime: 5 * 60 * 1000,
  })
}

export function useReachOutreachDepthQuery({from, to}: TReachRange) {
  return useQuery({
    queryKey: queryKey.reachOutreachDepth(from, to),
    queryFn: async () => (await api.reachEngine.outreachDepthList({from, to})).data.buckets,
    staleTime: 5 * 60 * 1000,
  })
}

export function useReachResultsOverTimeQuery({from, to}: TReachRange) {
  return useQuery({
    queryKey: queryKey.reachResultsOverTime(from, to),
    queryFn: async () => (await api.reachEngine.resultsOverTimeList({from, to})).data.buckets,
    staleTime: 5 * 60 * 1000,
  })
}
